// Lib
import React from 'react';
import { useDrop } from 'react-dnd';
import { round } from 'lodash';

// Utils
import * as pointLib from '../../../common/maths/geometry/point';
import { hasShiftKey } from '../../utils/keyboard/keyboardUtility';

// Measurements
import measurementsRegistry from '../../components/measurementsStore/measurementsRegistry';

// Selectors
import { getDraggedLineEdgesPx } from './lineEdgeSelector';
import { getDragModifierKeys } from '../../utils/dnd/modifierKeys/dragModifierKeysSelector';
import getGridSize from '../../utils/grid/gridSizeSelector';
import { getShallowMeasurementsMap } from '../../components/measurementsStore/elementMeasurements/elementMeasurementsSelector';

// Constants
import { LINE_EDGE_DND_TYPE, LINE_CONTROL_POINT_DND_TYPE } from '../../../common/lines/lineConstants';
import { NO_DRAG_OFFSET } from '../../utils/dnd/dndConstants';

/**
 * NOTE: This is being used in the Canvas's mapDispatchToProps, just so an extra connect
 *  wasn't added here (it's only every used on the canvas).
 */
export const getLineEdgeCanvasDropPosition =
    ({ pos, offsetDiff, scrollDiff, draggedEdge, draggedElement, initialClientOffset, initialSourceClientOffset }) =>
    (dispatch, getState, { measurementsStore }) => {
        const state = getState();
        const gridSize = getGridSize(state);

        const measurements = getShallowMeasurementsMap(measurementsStore.getState());

        const dragModifierKeys = getDragModifierKeys(state);
        const shouldSnapToAngle = hasShiftKey(dragModifierKeys);

        const dropEdgePositions = getDraggedLineEdgesPx(state, {
            element: draggedElement,
            draggedEdge,
            offsetDiff,
            scrollDiff,
            pos,
            initialClientOffset,
            initialSourceClientOffset,
            shouldSnapToAngle,
            measurements,
        });

        const dropPositionPx = dropEdgePositions[draggedEdge];

        const newPosition = {
            x: round(dropPositionPx.x / gridSize, 2),
            y: round(dropPositionPx.y / gridSize, 2),
            snapped: false,
        };

        return {
            newPosition,
            newControl: dropEdgePositions.updatedControl,
        };
    };

const canvasLineEdgeDropTargetSpec = {
    drop: (props, monitor) => {
        if (monitor.didDrop()) return;

        const { dispatchGetLineEdgeCanvasDropPosition } = props;

        const item = monitor.getItem();

        if (!item) return;

        const offsetDiff = monitor.getDifferenceFromInitialOffset() || NO_DRAG_OFFSET;

        // Control point dragging
        if (monitor.getItemType() === LINE_CONTROL_POINT_DND_TYPE) {
            return { offsetDiff };
        }

        // Edge point dragging
        const { pos, element, edge, initialScrollPoint } = item;

        const endScrollPoint = measurementsRegistry.getCanvasViewportScrollAsPoint();
        const scrollDiff = pointLib.difference(initialScrollPoint, endScrollPoint);

        const initialClientOffset = monitor.getInitialClientOffset();
        const initialSourceClientOffset = monitor.getInitialSourceClientOffset();

        const { newPosition, newControl } = dispatchGetLineEdgeCanvasDropPosition({
            pos,
            offsetDiff,
            scrollDiff,
            initialClientOffset,
            initialSourceClientOffset,
            draggedEdge: edge,
            draggedElement: element,
        });

        // NOTE: The quick line creation tool currently uses the offsetDiff, initialClientOffset
        //  and initialSourceClientOffset, but we should try to avoid this if possible
        return {
            offsetDiff,
            initialClientOffset,
            initialSourceClientOffset,
            newPosition,
            newControl,
        };
    },
};

const DROP_TYPES = [LINE_CONTROL_POINT_DND_TYPE, LINE_EDGE_DND_TYPE];

const CanvasLineEdgeDropTarget = (Element) => {
    const CanvasLineEdgeDropTarget = (props) => {
        const [dropProps, connector] = useDrop({
            accept: DROP_TYPES,
            drop: (item, monitor) => canvasLineEdgeDropTargetSpec.drop(props, monitor),
        });
        return <Element {...props} {...dropProps} connectLineEdgeDropTarget={connector} />;
    };
    return CanvasLineEdgeDropTarget;
};

export default CanvasLineEdgeDropTarget;
